import * as React from 'react'
import { cn } from '@/lib/utils'
import { IconButton } from '@/design-system/atoms/IconButton'
import { Icon } from '@/design-system/atoms/Icon'

export interface PaginationProps extends React.HTMLAttributes<HTMLElement> {
  page: number
  pageCount: number
  onPageChange: (page: number) => void
  siblingCount?: number
}

function range(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

function pagination(
  page: number,
  pageCount: number,
  siblings: number
): (number | 'ellipsis')[] {
  const total = siblings * 2 + 5
  if (pageCount <= total) return range(1, pageCount)

  const leftSibling = Math.max(page - siblings, 1)
  const rightSibling = Math.min(page + siblings, pageCount)
  const showLeftDots = leftSibling > 2
  const showRightDots = rightSibling < pageCount - 1

  if (!showLeftDots && showRightDots) {
    return [...range(1, 3 + siblings * 2), 'ellipsis', pageCount]
  }
  if (showLeftDots && !showRightDots) {
    return [
      1,
      'ellipsis',
      ...range(pageCount - (3 + siblings * 2) + 1, pageCount),
    ]
  }
  return [
    1,
    'ellipsis',
    ...range(leftSibling, rightSibling),
    'ellipsis',
    pageCount,
  ]
}

export const Pagination = React.forwardRef<HTMLElement, PaginationProps>(
  (
    { page, pageCount, onPageChange, siblingCount = 1, className, ...rest },
    ref
  ) => {
    const items = pagination(page, pageCount, siblingCount)
    const canPrev = page > 1
    const canNext = page < pageCount
    return (
      <nav
        ref={ref}
        aria-label="Paginação"
        className={cn('flex items-center gap-1', className)}
        {...rest}
      >
        <IconButton
          variant="ghost"
          size="sm"
          icon={<Icon name="chevron-left" size={16} />}
          aria-label="Página anterior"
          disabled={!canPrev}
          onClick={() => onPageChange(page - 1)}
        />
        {items.map((item, i) =>
          item === 'ellipsis' ? (
            <span
              key={`e-${i}`}
              aria-hidden="true"
              className="px-2 text-gray-400"
            >
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              aria-current={item === page ? 'page' : undefined}
              aria-label={`Ir para página ${item}`}
              onClick={() => onPageChange(item)}
              className={cn(
                'inline-flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                item === page
                  ? 'bg-primary-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
              )}
            >
              {item}
            </button>
          )
        )}
        <IconButton
          variant="ghost"
          size="sm"
          icon={<Icon name="chevron-right" size={16} />}
          aria-label="Próxima página"
          disabled={!canNext}
          onClick={() => onPageChange(page + 1)}
        />
      </nav>
    )
  }
)

Pagination.displayName = 'Pagination'
